import { useCallback, useState } from 'react';
import { ApiError } from '@bymason/utils';
import { getErrorCode, getErrorData, getErrorMessage } from './utils';
import { GraphQLError } from 'graphql';

export { GraphQLError } from 'graphql';
export interface GraphQLResponse<TData = unknown> {
  data?: TData;
  errors?: GraphQLError[];
  extensions?: unknown;
  status: number;
  [key: string]: unknown;
}

export type GqlQueryMurationHook<CriteriaType, ResultType, ErrorDataType> = [
  execute: (criteria: CriteriaType, signal?: AbortSignal | null) => void,
  result: ResultType,
  error: string | null,
  busy: boolean,
  errorCode: number,
  errorData: ErrorDataType | GraphQLError[] | undefined,
  clearData: () => void
];

export const GRAPHQL_API_ERRORS_FLAG = 'graphql-api-errors';

export function useGqlQueryMutationHook<O, T, E = void>(
  onGetGqlCallback: (options: O, signal?: AbortSignal | null) => Promise<void | GraphQLResponse<T>>
) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [errorCode, setErrorCode] = useState(0);
  const [errorData, setErrorData] = useState<E | GraphQLError[]>();

  const getData = useCallback(
    async (options: O, signal?: AbortSignal | null) => {
      try {
        setBusy(true);
        setError(null);
        setErrorCode(0);

        const gqlResp = await onGetGqlCallback(options, signal);

        if (gqlResp?.errors) {
          setError(GRAPHQL_API_ERRORS_FLAG);
          setErrorData(gqlResp.errors);
        } else {
          setData(gqlResp?.data || null);
        }
      } catch (error) {
        const err = error as Error | ApiError<E>;
        if (err.name === 'AbortError') {
          setData(null);
        } else {
          setError(getErrorMessage(err));
          setErrorCode(getErrorCode(err));
          setErrorData(getErrorData<E>(err));
        }
      } finally {
        setBusy(false);
      }
    },
    [onGetGqlCallback]
  );

  const clearData = useCallback(() => {
    setData(null);
  }, []);

  return [getData, data, error, busy, errorCode, errorData, clearData] as GqlQueryMurationHook<
    O,
    void | T,
    E
  >;
}
